import {CSSProperties, useMemo} from 'react';
import {Link, Image} from '@shopify/hydrogen';
import VectorIcon from '../VectorIcon';
import ReactModal from 'react-modal';

import {ProductReview} from '../../types';
import {PlaceholderWhiskyJPEG, UserDefaultJPEG} from '../../assets/img';

interface ProductReviewModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  productReview: ProductReview;
}

export default function ProductReviewModal({
  isOpen,
  onRequestClose,
  productReview,
}: ProductReviewModalProps) {
  return (
    <ReactModal
      isOpen={isOpen}
      className="modal__content modal-review-inner default-modal"
      overlayClassName="modal modal_active modal-review product-page-modal-review"
      shouldCloseOnOverlayClick={true}
      onRequestClose={onRequestClose}
      closeTimeoutMS={300}
    >
      <div
        role="button"
        tabIndex={-1}
        onClick={onRequestClose}
        className="default-modal__close close js-modal-close"
      >
        <VectorIcon iconName="delete-x" />
      </div>
      {productReview && <ProductReviewInfo productReview={productReview} />}
    </ReactModal>
  );
}

function ProductReviewInfo({productReview}) {
  const {
    rating,
    title,
    description,
    aroma_enjoyment_score,
    taste_enjoyment_score,
    balance_and_complexity_score,
    body_and_mouthfeel_score,
    finish_length_score,
    reviewer_image,
    reviewed_by,
    product: {title: productTitle, main_image, handle},
    aroma_notes,
    taste_notes,
    finish_notes,
  } = productReview;

  const hasTastingNotes = useMemo(
    () =>
      aroma_notes.length > 0 ||
      taste_notes.length > 0 ||
      finish_notes.length > 0,
    [aroma_notes, taste_notes, finish_notes],
  );

  return (
    <>
      <div className="default-modal__box modal-review-header">
        <div className="modal-review-images">
          <div className="modal-review-image modal-review-image-product">
            <Image
              src={main_image || PlaceholderWhiskyJPEG}
              width={100}
              height={100}
              alt="Product"
            />
          </div>
          <div className="modal-review-image modal-review-image-user">
            <Image
              src={reviewer_image || UserDefaultJPEG}
              width={100}
              height={100}
              alt="User"
            />
          </div>
        </div>
        <div className="modal-review-author" id="review-author">
          {reviewed_by}
        </div>
        <div
          className="modal-review-product default-modal__title"
          id="review-product"
        >
          <Link to={`/shop/${handle}`} id="review-url">
            {productTitle}
          </Link>
        </div>
      </div>
      <div className="modal-review-content default-modal__box">
        {rating && rating > 0 && (
          <>
            <div className="modal-review-score">
              <span id="review-score">{rating} </span>/5
            </div>
            <div className="modal-review-rating" id="modal-review-rating">
              <div
                className="rating-graph"
                id="review-average"
                style={{'--rating': rating} as CSSProperties}
              />
            </div>
          </>
        )}
        <div className="modal-review-title" id="review-title">
          {title}
        </div>
        <div className="modal-review-text" id="review-text">
          {description}
        </div>
        <div className="modal-review-scores">
          {aroma_enjoyment_score && (
            <div className="modal-review-score-item">
              <div className="modal-review-score-item-name">
                <div className="modal-review-score-item__title">
                  Aroma Enjoyment
                </div>
                <div className="modal-rating-score-item__help tooltip tooltip-trigger">
                  <span className="tooltiptext">
                    A score of the overall enjoyment of smelling a whisky. The
                    longer you want to sit and sniff, the more you simply enjoy
                    the aromas, the higher the value.
                  </span>
                </div>
              </div>
              <div
                className="rating-graph"
                id="review-aroma"
                style={{'--rating': aroma_enjoyment_score} as CSSProperties}
              />
            </div>
          )}
          {taste_enjoyment_score && (
            <div className="modal-review-score-item">
              <div className="modal-review-score-item-name">
                <div className="modal-review-score-item__title">
                  Taste Enjoyment
                </div>
                <div className="modal-rating-score-item__help tooltip tooltip-trigger">
                  <span className="tooltiptext">
                    This is about the overall enjoyment of the flavours.
                    It&amp;s the simple measurement of how nice the whisky
                    tastes and how much you enjoy the flavours.
                  </span>
                </div>
              </div>
              <div
                className="rating-graph"
                id="review-taste"
                style={{'--rating': taste_enjoyment_score} as CSSProperties}
              />
            </div>
          )}
          {body_and_mouthfeel_score && (
            <div className="modal-review-score-item">
              <div className="modal-review-score-item-name">
                <div className="modal-review-score-item__title">
                  Body &amp; Mouthfeel
                </div>
                <div className="modal-rating-score-item__help tooltip tooltip-trigger">
                  <span className="tooltiptext">
                    A textural evaluation of a whisky, describing the weight of
                    a whisky in the mouth and how will the viscosity of a whisky
                    holds throughout the palate. Thin feeling, light weighing,
                    watery whisky would receive lower points while thick
                    feeling, heavy weighing viscous whisky would receive higher
                    points. A whisky that gives way to too much dryness or
                    tannin would receive lower points, while one holding a
                    solid, balanced structure of viscosity and/or tannin would
                    receive higher points.
                  </span>
                </div>
              </div>
              <div
                className="rating-graph"
                id="review-mouthfeel"
                style={{'--rating': body_and_mouthfeel_score} as CSSProperties}
              />
            </div>
          )}
          {balance_and_complexity_score && (
            <div className="modal-review-score-item">
              <div className="modal-review-score-item-name">
                <div className="modal-review-score-item__title">
                  Balance &amp; Complexity
                </div>
                <div className="modal-rating-score-item__help tooltip tooltip-trigger">
                  <span className="tooltiptext">
                    Measures the variety of flavours across nose, palate and
                    finish and how well the flavours work together; from spirit,
                    time and oak. A simple whisky, one with fewer
                    distinguishable flavours would receive lower points, while a
                    more complex whisky with a wide variety of flavour working
                    together would receive a higher score.
                  </span>
                </div>
              </div>
              <div
                className="rating-graph"
                id="review-balance"
                style={
                  {
                    '--rating': balance_and_complexity_score,
                  } as CSSProperties
                }
              />
            </div>
          )}
          {finish_length_score && (
            <div className="modal-review-score-item">
              <div className="modal-review-score-item-name">
                <div className="modal-review-score-item__title">
                  Finish / Length
                </div>
                <div className="modal-rating-score-item__help tooltip tooltip-trigger">
                  <span className="tooltiptext">
                    Part of the palate evaluation of a whisky, length describes
                    how long the flavour of a whisky continuous to deliver
                    through the palate and finish. Whiskies where the flavour
                    dissipates quickly would receive lower points, while a
                    longer, lingering flavour would receive more points.
                  </span>
                </div>
              </div>
              <div
                className="rating-graph"
                id="review-finish"
                style={{'--rating': finish_length_score} as CSSProperties}
              />
            </div>
          )}
        </div>
        {hasTastingNotes && (
          <div className="tasting-notes">
            <h6>Tasting notes</h6>
            {aroma_notes.length > 0 && (
              <>
                <div className="tasting-notes__title">Aroma</div>
                <div className="tasting-notes__row">
                  {aroma_notes.map((note) => (
                    <div key={note} className="tasting-notes__item">
                      {note}
                    </div>
                  ))}
                </div>
              </>
            )}
            {taste_notes.length > 0 && (
              <>
                <div className="tasting-notes__title">Taste</div>
                <div className="tasting-notes__row">
                  {taste_notes.map((note) => (
                    <div key={note} className="tasting-notes__item">
                      {note}
                    </div>
                  ))}
                </div>
              </>
            )}
            {finish_notes.length > 0 && (
              <>
                <div className="tasting-notes__title">Finish</div>
                <div className="tasting-notes__row">
                  {finish_notes.map((note) => (
                    <div key={note} className="tasting-notes__item">
                      {note}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
